import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, FormControl, Grid, InputLabel, Select, Tab, Tabs, TextField, Typography } from "@mui/material";
import OperacionesBitacora from "./Operaciones";
import { Search as IconSearch, RestartAlt as IconRestart } from "@mui/icons-material";
import moment from "moment";

import * as bitacoraService from "../../services/bitacoraService";
import DataGridBase from "../../components/common/DataGridBase";

// Navegacion de la bitacora de cambios (tabla bitacoracambio).
//
// Es de solo lectura: muestra que registro cada usuario en cada tabla y campo,
// con el valor antes y despues. No hay Nuevo, Editar ni Eliminar, porque el
// que escribe la bitacora es el interceptor de SaveChanges y los registros no
// se tocan a mano.
//
// Las columnas de auditoria de la fila (quien la creo y quien la toco por
// ultima vez) las agrega el grid con el boton del ojo, y empiezan ocultas.
const FILTROS_VACIOS = {
    fechaInicio: "",
    fechaFin: "",
    tabla: "",
    campo: "",
    llaves: "",
    usuario: "",
    operacion: "",
};

const OPERACIONES = [
    { id: "", descripcion: "(Todas)" },
    { id: "Alta", descripcion: "Alta" },
    { id: "Modificacion", descripcion: "Modificacion" },
    { id: "Baja", descripcion: "Baja" },
];

// Un valor de la bitacora que es una fecha ISO se muestra dia/mes/anio.
// El resto (textos, numeros, claves) se deja como llego.
const formatearValorBitacora = (valor) => {
    if (valor == null || valor === "") return "";
    const texto = String(valor).trim();
    if (!/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/.test(texto)) return texto;

    const fecha = moment(texto.replace(" ", "T"));
    if (!fecha.isValid()) return texto;

    const tieneHora = /[T ]\d{2}:\d{2}/.test(texto) && !/00:00:00$/.test(texto);
    return fecha.format(tieneHora ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
};

const Bitacora = () => {
    const [pestana, setPestana] = useState(0);
    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);
    const [filtros, setFiltros] = useState(FILTROS_VACIOS);

    const fetchResults = useCallback(async (filtrosAplicados) => {
        setCargando(true);

        const data = await bitacoraService.getsBitacoraCambio(filtrosAplicados)
            .catch((err) => { console.log("Err: ", err); return []; });

        // El grid pide un id unico por fila: es el IdBitacora.
        let lista = (data || []).map((row) => ({ ...row, id: row.idBitacora }));

        setResults(lista);
        setCargando(false);
    }, [])

    useEffect(() => {
        fetchResults({ ...FILTROS_VACIOS, limite: 50 });
    }, []);

    const cambiarFiltro = (nombre, valor) => {
        setFiltros((anterior) => ({ ...anterior, [nombre]: valor }));
    }

    const limpiar = () => {
        setFiltros(FILTROS_VACIOS);
        fetchResults({ ...FILTROS_VACIOS, limite: 50 });
    }

    const columns = [
        { field: 'fechaHoraUtc', headerName: 'Fecha y hora', flex: 0, width: 132, minWidth: 132, sortable: false, headerAlign: 'left',
            valueFormatter: (valor) => (valor && moment.utc(valor).isValid() ? moment.utc(valor).format("DD/MM/YYYY HH:mm") : "") },
        { field: 'tabla', headerName: 'Tabla', flex: 1, minWidth: 108, sortable: false, headerAlign: 'left' },
        { field: 'modulo', headerName: 'Modulo', flex: 0.8, minWidth: 96, sortable: false, headerAlign: 'left' },
        { field: 'campo', headerName: 'Campo', flex: 0.9, minWidth: 96, sortable: false, headerAlign: 'left' },
        { field: 'llaves', headerName: 'Llaves', flex: 1.4, minWidth: 140, sortable: false, headerAlign: 'left' },
        { field: 'operacion', headerName: 'Operacion', flex: 0, width: 116, minWidth: 116, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'valorAnterior', headerName: 'Valor anterior', flex: 1.3, minWidth: 112, sortable: false, headerAlign: 'left',
            valueFormatter: formatearValorBitacora,
            renderCell: (params) => {
                const texto = formatearValorBitacora(params.value);
                return <span title={texto} style={{ whiteSpace: "normal", display: "block", wordBreak: "break-word", lineHeight: 1.3 }}>{texto}</span>;
            } },
        { field: 'valorPosterior', headerName: 'Valor posterior', flex: 1.3, minWidth: 118, sortable: false, headerAlign: 'left',
            valueFormatter: formatearValorBitacora,
            renderCell: (params) => {
                const texto = formatearValorBitacora(params.value);
                return <span title={texto} style={{ whiteSpace: "normal", display: "block", wordBreak: "break-word", lineHeight: 1.3 }}>{texto}</span>;
            } },
        { field: 'usuario', headerName: 'Usuario', flex: 0, width: 74, minWidth: 74, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'requestId', headerName: 'RequestId', flex: 0, width: 90, minWidth: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'idSesion', headerName: 'IdSesion', flex: 0, width: 80, minWidth: 80, sortable: false, headerAlign: 'center', align: 'center' },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Bitacora de Cambios
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ paddingTop: "6px" }}>
                    <Typography variant="caption" color="text.secondary">
                        Cada cambio que un usuario guarda en el sistema. Solo lectura.
                    </Typography>
                </Grid>

                {/* El rango de fechas va primero: es el filtro que mas se usa. */}
                <Grid container spacing={1} padding={"8px 0px 0px 0px"}>
                    <Grid item xs={6} sm={4} md={3} lg={2}>
                        <TextField name="fechaInicio" label="Desde" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={filtros.fechaInicio}
                            onChange={(event) => cambiarFiltro("fechaInicio", event.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2}>
                        <TextField name="fechaFin" label="Hasta" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={filtros.fechaFin}
                            onChange={(event) => cambiarFiltro("fechaFin", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3} lg={2}>
                        <TextField name="tabla" label="Tabla" fullWidth size="small" value={filtros.tabla}
                            onChange={(event) => cambiarFiltro("tabla", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3} lg={2}>
                        <TextField name="campo" label="Campo" fullWidth size="small" value={filtros.campo}
                            onChange={(event) => cambiarFiltro("campo", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4} lg={2}>
                        <TextField name="llaves" label="Llaves" fullWidth size="small" value={filtros.llaves}
                            onChange={(event) => cambiarFiltro("llaves", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4} lg={2}>
                        <TextField name="usuario" label="Usuario" fullWidth size="small" value={filtros.usuario}
                            onChange={(event) => cambiarFiltro("usuario", event.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4} lg={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>Operacion</InputLabel>
                            <Select native label="Operacion" value={filtros.operacion}
                                onChange={(event) => cambiarFiltro("operacion", event.target.value)}>
                                {OPERACIONES.map((op) => (
                                    <option key={op.id} value={op.id}>{op.descripcion}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Grid container spacing={1} padding={"4px 0px 4px 0px"}>
                    <Grid item xs={6} sm={1}>
                        <Button variant="outlined" fullWidth size="medium" onClick={() => fetchResults({ ...filtros, limite: 50 })}>
                            <IconSearch />
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={1}>
                        <Button variant="outlined" fullWidth size="medium" onClick={limpiar}>
                            <IconRestart />
                        </Button>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        <DataGridBase
                            rows={results}
                            columns={columns}
                            loading={cargando}
                            disableRowSelectionOnClick={false}
                            showToolbar={true}
                            getRowHeight={() => "auto"}
                            sx={{
                                "& .MuiDataGrid-cell": {
                                    alignItems: "center",
                                    py: "4px",
                                },
                                "& .MuiDataGrid-cellContent": {
                                    whiteSpace: "normal !important",
                                    wordBreak: "break-word",
                                    lineHeight: "1.3",
                                    overflow: "visible !important",
                                    textOverflow: "clip !important",
                                },
                            }}
                            // Aqui el listado se ordena por la fecha del cambio,
                            // no por la fecha de registro de la fila auditada.
                            campoOrden="fechaHoraUtc"
                        />
                    </Box>
                </Grid>
            </Grid>
            )}
        </div>
    );
}

export default Bitacora;
